import { RoadmapItem } from '@/models/RoadmapItem';
import {
    Checkbox,
    ListItemText,
    MenuItem,
    MenuItemProps,
    Select,
    SelectChangeEvent,
    TextField
} from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { ChangeEvent, ReactNode, useMemo } from 'react';

interface FilterViewProps {
    roadmapData: RoadmapItem[],
    filterState: FilterState,
    setFilterState: React.Dispatch<React.SetStateAction<FilterState>>,
}

type FilterState = {
    searchString: string,
    categories: string[],
    products: string[],
    status: string,
}

const SELECT_ALL_VALUE = "Select All"
export const ALL_STATUSES_VALUE = "All Statuses"

export const DefaultFilterState: FilterState = {
    searchString: '',
    categories: [],
    products: [],
    status: ALL_STATUSES_VALUE,
}

const getUniqueValuesByKey = (collection: any[], key: string) => {
    return [...new Set(collection.map(x => x[key]))]
}

const caseInsensitiveSort = (str: string[]) => {
    return str.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
}

const renderSelectText = (value: string[], type: string): ReactNode => {
    if (value.length === 0) {
        return "All " + type
    }

    return `${value.length} selected`
}

const FilterView: React.FC<FilterViewProps> = ({
    roadmapData,
    filterState,
    setFilterState
}) => {
    const categories = useMemo(() => {
        const uniqueValues = getUniqueValuesByKey(roadmapData, 'category')
        return caseInsensitiveSort(uniqueValues)
    }, [roadmapData])

    const products = useMemo(() => {
        const uniqueValues = getUniqueValuesByKey(roadmapData, 'product')
        return caseInsensitiveSort(uniqueValues)
    }, [roadmapData])

    const statuses = ["Coming Soon", "Further Out", "New Release", "Released"]

    const handleCategoryChange = (event: SelectChangeEvent<string[]>, child: ReactNode) => {
        const { value } = event.target
        const valueAsArray = typeof value === 'string' ? value.split(',') : value

        const selectedChild = child as React.ReactElement<MenuItemProps>
        if (selectedChild.props.value === SELECT_ALL_VALUE) {
            if (valueAsArray.includes(SELECT_ALL_VALUE)) {
                setFilterState({ ...filterState, categories: [...categories, SELECT_ALL_VALUE] })
            }
            else {
                setFilterState({ ...filterState, categories: [] })
            }
            return
        }
        setFilterState({ ...filterState, categories: valueAsArray })
    }

    const handleProductChange = (event: SelectChangeEvent<string[]>, child: ReactNode) => {
        const { value } = event.target
        const valueAsArray = typeof value === 'string' ? value.split(',') : value

        const selectedChild = child as React.ReactElement<MenuItemProps>
        if (selectedChild.props.value === SELECT_ALL_VALUE) {
            if (valueAsArray.includes(SELECT_ALL_VALUE)) {
                setFilterState({ ...filterState, products: [...products, SELECT_ALL_VALUE] })
            }
            else {
                setFilterState({ ...filterState, products: [] })
            }
            return
        }
        setFilterState({ ...filterState, products: valueAsArray })
    }

    const handleStatusChange = (event: SelectChangeEvent<string>) => {
        const { value } = event.target
        setFilterState({ ...filterState, status: value })
    }

    function handleSearchStringChange(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
        const { value } = event.target
        setFilterState({ ...filterState, searchString: value })
    }

    return <>
        <Grid container spacing={1}>
            <Grid size={4}>
                <TextField
                    fullWidth
                    value={filterState.searchString}
                    onChange={handleSearchStringChange} />
            </Grid>
            <Grid size={2}>
                <Select<string[]>
                    fullWidth
                    multiple
                    displayEmpty
                    value={filterState.categories}
                    onChange={handleCategoryChange}
                    renderValue={(v) => renderSelectText(v, "Categories")}
                >
                    <MenuItem key={SELECT_ALL_VALUE} value={SELECT_ALL_VALUE}>
                        <Checkbox checked={filterState.categories.includes(SELECT_ALL_VALUE)} />
                        <ListItemText primary={SELECT_ALL_VALUE} />
                    </MenuItem>
                    {categories.map((item) => (
                        <MenuItem key={item} value={item}>
                            <Checkbox checked={filterState.categories.includes(item)} />
                            <ListItemText primary={item} />
                        </MenuItem>
                    ))}
                </Select>
            </Grid>
            <Grid size={2}>
                <Select<string[]>
                    fullWidth
                    multiple
                    displayEmpty
                    value={filterState.products}
                    onChange={handleProductChange}
                    renderValue={(v) => renderSelectText(v, "Products")}
                >
                    <MenuItem key={SELECT_ALL_VALUE} value={SELECT_ALL_VALUE}>
                        <Checkbox checked={filterState.products.includes(SELECT_ALL_VALUE)} />
                        <ListItemText primary={SELECT_ALL_VALUE} />
                    </MenuItem>
                    {products.map((item) => (
                        <MenuItem key={item} value={item}>
                            <Checkbox checked={filterState.products.includes(item)} />
                            <ListItemText primary={item} />
                        </MenuItem>
                    ))}
                </Select>
            </Grid>
            {filterState.status !== "Released" && (
                <Grid size={2}>
                    <Select
                        fullWidth
                        value={filterState.status}
                        onChange={handleStatusChange}
                    >
                        <MenuItem key={ALL_STATUSES_VALUE} value={ALL_STATUSES_VALUE}>
                            {ALL_STATUSES_VALUE}
                        </MenuItem>
                        {statuses.map((item) => (
                            <MenuItem key={item} value={item}>
                                {item}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
            )}
        </Grid>
    </>
}

export default FilterView